import { camelizeKeys, decamelizeKeys } from 'humps';

import { configuration } from '../configuration';
import { version } from '../version';
import { salsifyId, uuid } from '../functions';
import { HttpError } from './interfaces';

const UNAUTHORIZED_DOMAIN_ERROR = 'Unauthorized domain';

export class BaseAPI {
  protected static authorizedDomain = true;
  protected model = '';
  protected prefix = 'api/v2';
  protected suffix = '';

  protected get host(): string {
    return configuration.apiUrl;
  }

  async get<T>(path, queryParams = {}, options: any = {}): Promise<T> {
    return await this.request<T>(this.url(path, queryParams, options), {
      ...options,
      method: 'GET',
    });
  }

  async post<T>(path, body = {}, options: any = {}): Promise<T> {
    return await this.request<T>(this.url(path), {
      body: await this.body(body),
      method: 'POST',
      ...options,
    });
  }

  async put<T>(path, body = {}, options: any = {}): Promise<T> {
    return await this.request<T>(this.url(path), {
      body: await this.body(body),
      method: 'PUT',
      ...options,
    });
  }

  async request<T>(url, allOptions: any = {}): Promise<T> {
    if (!BaseAPI.authorizedDomain) return;

    const { decamelizeKeys, ...options } = allOptions;

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-App': 'liquid-cloud',
      'X-Version': version,
      ...(await this.headers()),
    };

    if (typeof options.body === 'object') {
      options.body = JSON.stringify(this.decamelizeKeys(options.body, allOptions));
    }
    const response = await fetch(url.toString(), { headers, ...options });
    const responseBody = await response.json();

    if (response.status >= 200 && response.status < 300) {
      return camelizeKeys(responseBody, (key, convert) =>
        uuid(key) || salsifyId(key) ? key : convert(key),
      ) as T;
    }

    if (response.status === 403 && responseBody.error === UNAUTHORIZED_DOMAIN_ERROR) {
      console.error(UNAUTHORIZED_DOMAIN_ERROR);
      BaseAPI.authorizedDomain = false;
    }

    throw new HttpError({
      statusText: '',
      error: responseBody?.error || {
        message: Object.values(responseBody?.errors || {}).join(', '),
      },
      status: response.status,
      url,
      headers,
    });
  }

  protected async headers(): Promise<Record<string, string>> {
    return {};
  }

  protected async minibarHeaders(): Promise<Record<string, string>> {
    const token = await configuration.credentials.getToken();
    return { Authorization: `Bearer ${token.accessToken}` };
  }

  protected getClientId() {
    return configuration.credentials?.clientId;
  }

  protected url(path: string, params = {}, options = {}): URL {
    params = this.decamelizeKeys(params, options);
    const url = new URL(
      [this.host, this.prefix, this.model, path, this.suffix].filter((x) => x).join('/'),
    );
    Object.keys(params).forEach((key) => this.handleSearchParams(url, key, params[key]));
    return url;
  }

  protected handleSearchParams(url, key, value) {
    if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(`${key}[]`, v));
    else url.searchParams.append(key, value);
  }

  protected async body(params: any) {
    return params;
  }

  protected decamelizeKeys(
    params: any,
    options: { decamelizeKeys?: boolean; [other: string | number | symbol]: any },
  ) {
    return options.decamelizeKeys !== false ? decamelizeKeys(params) : params;
  }
}

export default BaseAPI;
